import { useCallback, useEffect, useState } from 'react';
import { Handshake, FileText, Calculator, Receipt, History, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { invalidateNavAccess } from '../lib/nav';
import TermsTab from '../components/partner-comp/TermsTab';
import ScenariosTab from '../components/partner-comp/ScenariosTab';
import SharesTab from '../components/partner-comp/SharesTab';
import { parseBps, inputCls, btnCls, btn2Cls, cardCls, fmtDate, Empty } from '../components/partner-comp/shared';

// Partner pay: the partners negotiate their pay agreement here. Edit the draft terms, see what
// they pay on real deals, received payments or a typed amount, each mark agree, and Chad locks
// a dated read-only version. Shares are worked out for every payment received. Visible only to
// the partners named on the agreement and to the owner; the server enforces it. Nothing here
// pays anyone.

const TABS = [
  { key: 'terms', label: 'Terms', icon: FileText },
  { key: 'scenarios', label: 'Scenarios', icon: Calculator },
  { key: 'shares', label: 'Shares per payment', icon: Receipt },
  { key: 'history', label: 'Version history', icon: History },
];

export default function PartnerCompPage() {
  const [me, setMe] = useState(null);
  const [list, setList] = useState(null);
  const [agId, setAgId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [versionId, setVersionId] = useState(null);
  const [tab, setTab] = useState('terms');
  const [termsDirty, setTermsDirty] = useState(false);
  const [error, setError] = useState('');
  // Unsaved draft edits survive a tab switch (the Terms tab stays mounted); leaving the
  // version or the agreement asks first.
  const okToLeave = () => !termsDirty || window.confirm('You have unsaved changes to the draft. Discard them?');

  const loadList = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([api.get('/partner-comp/me'), api.get('/partner-comp/agreements')]);
      setMe(m); setList(l.agreements); setError('');
      setAgId((cur) => cur || l.agreements[0]?.id || null);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback(async () => {
    if (!agId) { setDetail(null); return; }
    try {
      const d = await api.get(`/partner-comp/agreements/${agId}`);
      setDetail(d); setError('');
      setVersionId((cur) => (d.versions.some((v) => v.id === cur) ? cur : (d.versions.find((v) => v.status === 'draft') || d.versions[0])?.id || null));
    } catch (e) { setError(e.message); }
  }, [agId]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  if (error && !me) return <div className="p-4 text-rose-600">{error}</div>;
  if (!me || !list) return <div className="p-4 text-[var(--text-secondary)]">Loading...</div>;
  if (!me.can_view) return <div className="p-4 text-[var(--text-secondary)]">Partner pay is visible to the partners named on the agreement and to the owner only.</div>;

  const version = detail?.versions.find((v) => v.id === versionId) || null;
  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><Handshake size={20} /> Partner pay</h1>
        <p className="text-sm text-[var(--text-secondary)]">The partners' pay agreement: edit the draft, see what it pays, and mark agree. Chad locks a dated version once everyone agrees; later changes start a new draft. Nothing here pays anyone.</p>
      </div>
      {error && <p className="mb-2 text-sm text-rose-600">{error}</p>}

      {!list.length ? (
        me.is_owner ? <CreateAgreement onCreated={async (id) => { invalidateNavAccess(); setAgId(id); await loadList(); }} /> : <Empty>No agreement yet. Chad starts it.</Empty>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {list.length > 1 && (
              <select className={inputCls} value={agId || ''} onChange={(e) => { if (okToLeave()) { setAgId(e.target.value); setVersionId(null); } }} aria-label="Agreement">
                {list.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            )}
            {detail && (
              <select className={inputCls} value={versionId || ''} onChange={(e) => { if (okToLeave()) setVersionId(e.target.value); }} aria-label="Version">
                {detail.versions.map((v) => <option key={v.id} value={v.id}>Version {v.version_no}: {v.status === 'draft' ? 'draft' : `locked ${fmtDate(v.locked_on)}`}</option>)}
              </select>
            )}
            {detail && <span className="text-sm text-[var(--text-secondary)]">Partners: {detail.partners.map((p) => p.partner).join(', ')}{detail.me.partner ? ` (you are ${detail.me.partner})` : ''}</span>}
            {detail?.partners.filter((p) => p.email_changed).map((p) => (
              <span key={p.partner} className="w-full text-xs text-amber-700 dark:text-amber-400">{p.partner}'s sign-in email was changed to {p.email} by {p.email_changed.by} on {fmtDate(String(p.email_changed.at).slice(0, 10))}.</span>
            ))}
          </div>
          <div className="mb-4 flex gap-1 overflow-x-auto border-b border-[var(--border)]">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${tab === key ? 'border-primary text-primary' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
          {detail && version && <div hidden={tab !== 'terms'}><TermsTab detail={detail} version={version} onChanged={loadDetail} onDirtyChange={setTermsDirty} /></div>}
          {detail && version && tab === 'scenarios' && <ScenariosTab detail={detail} version={version} termsDirty={termsDirty} />}
          {detail && tab === 'shares' && <SharesTab detail={detail} />}
          {detail && tab === 'history' && <VersionHistory detail={detail} onOpen={(id) => { if (id === versionId || okToLeave()) { setVersionId(id); setTab('terms'); } }} />}
        </>
      )}
    </div>
  );
}

function VersionHistory({ detail, onOpen }) {
  return (
    <div className="space-y-2">
      {detail.versions.map((v) => {
        const from = v.based_on_version_id ? detail.versions.find((x) => x.id === v.based_on_version_id) : null;
        return (
          <div key={v.id} className={cardCls}>
            <div className="flex flex-wrap items-baseline gap-2">
              <strong className="text-[var(--text-primary)]">Version {v.version_no}</strong>
              <span className={`rounded px-1.5 py-0.5 text-xs ${v.status === 'locked' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>{v.status === 'locked' ? `Locked ${fmtDate(v.locked_on)}` : 'Draft'}</span>
              {from && <span className="text-xs text-[var(--text-secondary)]">copied from version {from.version_no}</span>}
              <button className="ml-auto text-sm text-primary" onClick={() => onOpen(v.id)}>Open</button>
            </div>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              Created {fmtDate(String(v.created_at).slice(0, 10))} by {v.created_by || 'unknown'}; last edited by {v.updated_by || 'unknown'}.
              {v.status === 'locked' ? ` Locked by ${v.locked_by}; fingerprint ${v.snapshot_sha256?.slice(0, 16)}...` : ` Terms revision ${v.terms_revision}.`}
            </p>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              Agree marks: {v.marks.length ? v.marks.map((m) => `${m.partner} (${fmtDate(String(m.agreed_at).slice(0, 10))}${m.current ? '' : ', earlier terms'})`).join(', ') : 'none'}.
              {v.status === 'draft' && v.pending_partners.length ? ` Waiting for ${v.pending_partners.join(', ')}.` : ''}
            </p>
            {v.comments && <p className="mt-1 text-sm text-[var(--text-primary)]">{v.comments}</p>}
          </div>
        );
      })}
    </div>
  );
}

function CreateAgreement({ onCreated }) {
  const [title, setTitle] = useState('Partner pay agreement');
  const [rows, setRows] = useState([{ partner: '', email: '', pct: '' }]);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState('sales_delivery');
  const [opsHolder, setOpsHolder] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/partner-comp/templates').then((t) => setTemplates(t.templates)).catch((e) => setError(e.message)); }, []);
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const create = async () => {
    const partners = rows.map((r) => ({ partner: r.partner.trim(), email: r.email.trim(), ownership_bps: parseBps(r.pct) }));
    if (partners.some((p) => p.ownership_bps == null)) { setError('Each ownership share must be a percent like 40'); return; }
    setBusy(true); setError('');
    try {
      const out = await api.post('/partner-comp/agreements', { title, partners, template, ops_holder: opsHolder || undefined });
      await onCreated(out.agreement.id);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className={`${cardCls} max-w-2xl space-y-3`}>
      <h2 className="font-semibold text-[var(--text-primary)]">Start the agreement</h2>
      <p className="text-sm text-[var(--text-secondary)]">Name the partners: only they (and you) will see it. The starting point only pre-fills the first draft; every rate and amount stays editable until everyone agrees.</p>
      <input className={`${inputCls} w-full`} value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
            <input className={`${inputCls} w-32`} placeholder="Name" value={r.partner} onChange={(e) => set(i, 'partner', e.target.value)} aria-label="Partner name" />
            <input className={`${inputCls} w-64`} placeholder="Sign-in email" value={r.email} onChange={(e) => set(i, 'email', e.target.value)} aria-label="Partner email" />
            <input className={`${inputCls} w-20 text-right`} placeholder="40" value={r.pct} onChange={(e) => set(i, 'pct', e.target.value)} aria-label="Ownership percent" />%
            {rows.length > 1 && <button className="text-rose-600" aria-label="Remove partner" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}><Trash2 size={14} /></button>}
          </div>
        ))}
        {rows.length < 10 && <button className={btn2Cls} onClick={() => setRows((r) => [...r, { partner: '', email: '', pct: '' }])}><Plus size={14} /> Add a partner</button>}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>Start from</span>
        <select className={inputCls} value={template} onChange={(e) => setTemplate(e.target.value)} aria-label="Starting point">
          {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <span>Operations held by</span>
        <select className={inputCls} value={opsHolder} onChange={(e) => setOpsHolder(e.target.value)} aria-label="Operations holder">
          <option value="">First partner</option>
          {rows.filter((r) => r.partner.trim()).map((r) => <option key={r.partner} value={r.partner.trim()}>{r.partner.trim()}</option>)}
        </select>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <button className={btnCls} disabled={busy} onClick={create}>Create the agreement (version 1, draft)</button>
    </div>
  );
}
